/**
 * geo.js — canonical coordinate parsing + validation for the map components.
 *
 * Mirrors backend lib/orderLogic.js so the customer/admin/trucker maps and the
 * server agree on exactly which coordinates may be plotted. The state guard is
 * the fix for the "Phoenix order plotted in San Francisco" defect: the old
 * North-America box accepted any US coordinate, so a mis-geocoded AZ site in CA
 * still showed a pin.
 */

export function parseLocation(data) {
  if (data === null || data === undefined) return null;
  let parsed = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch (e) {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;

  if (!Array.isArray(parsed) && (parsed.lat !== undefined || parsed.latitude !== undefined)) {
    const lat = parseFloat(parsed.lat ?? parsed.latitude);
    const lng = parseFloat(parsed.lng ?? parsed.longitude);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    return null;
  }
  if (!Array.isArray(parsed)) return null;

  if (parsed.length >= 2 && !Array.isArray(parsed[0]) && typeof parsed[0] !== "object") {
    const lat = Number(parsed[0]);
    const lng = Number(parsed[1]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    return null;
  }
  if (parsed.length > 0 && Array.isArray(parsed[0])) {
    return centroid(parsed.map((p) => (Array.isArray(p) ? { lat: Number(p[0]), lng: Number(p[1]) } : null)));
  }
  if (parsed.length > 0 && typeof parsed[0] === "object") {
    return centroid(parsed.map((p) => ({ lat: Number(p.lat ?? p.latitude), lng: Number(p.lng ?? p.longitude) })));
  }
  return null;
}

export function centroid(points) {
  let sumLat = 0, sumLng = 0, n = 0;
  for (const p of points) {
    if (p && !Number.isNaN(p.lat) && !Number.isNaN(p.lng)) { sumLat += p.lat; sumLng += p.lng; n++; }
  }
  return n ? { lat: sumLat / n, lng: sumLng / n } : null;
}

export function isValidNorthAmericaCoord(c) {
  if (!c || typeof c.lat !== "number" || typeof c.lng !== "number") return false;
  if (Number.isNaN(c.lat) || Number.isNaN(c.lng)) return false;
  if (c.lat === 0 && c.lng === 0) return false;
  return c.lat >= 15 && c.lat <= 72 && c.lng >= -170 && c.lng <= -50;
}

export const STATE_BBOX = {
  AZ: { latMin: 31.3, latMax: 37.1, lngMin: -114.9, lngMax: -109.0 },
  NV: { latMin: 35.0, latMax: 42.1, lngMin: -120.1, lngMax: -114.0 },
  CA: { latMin: 32.5, latMax: 42.1, lngMin: -124.5, lngMax: -114.1 },
  UT: { latMin: 37.0, latMax: 42.1, lngMin: -114.1, lngMax: -109.0 },
};

/**
 * Full US state names → USPS codes.
 *
 * `customer_site.state` is free text. Production (2026-08-31) holds `AZ` 1177,
 * `NV` 259, `''` 181, NULL 21, plus a tail of `Arizona`, `Illinois`,
 * `Madhya Pradesh`, `Daasdasda`, ` NV`, `NVC`, … The old implementation was
 * `.slice(0, 2)`, which turned 'Arizona' into 'AR' and 'Nevada' into 'NE' —
 * neither a STATE_BBOX key — so a correctly geocoded site was silently refused
 * a pin. 'California'/'Utah' happened to survive by coincidence.
 */
const STATE_NAME_TO_CODE = {
  ALABAMA: "AL", ALASKA: "AK", ARIZONA: "AZ", ARKANSAS: "AR", CALIFORNIA: "CA",
  COLORADO: "CO", CONNECTICUT: "CT", DELAWARE: "DE", FLORIDA: "FL", GEORGIA: "GA",
  HAWAII: "HI", IDAHO: "ID", ILLINOIS: "IL", INDIANA: "IN", IOWA: "IA",
  KANSAS: "KS", KENTUCKY: "KY", LOUISIANA: "LA", MAINE: "ME", MARYLAND: "MD",
  MASSACHUSETTS: "MA", MICHIGAN: "MI", MINNESOTA: "MN", MISSISSIPPI: "MS",
  MISSOURI: "MO", MONTANA: "MT", NEBRASKA: "NE", NEVADA: "NV",
  "NEW HAMPSHIRE": "NH", "NEW JERSEY": "NJ", "NEW MEXICO": "NM", "NEW YORK": "NY",
  "NORTH CAROLINA": "NC", "NORTH DAKOTA": "ND", OHIO: "OH", OKLAHOMA: "OK",
  OREGON: "OR", PENNSYLVANIA: "PA", "RHODE ISLAND": "RI", "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD", TENNESSEE: "TN", TEXAS: "TX", UTAH: "UT", VERMONT: "VT",
  VIRGINIA: "VA", WASHINGTON: "WA", "WEST VIRGINIA": "WV", WISCONSIN: "WI",
  WYOMING: "WY", "DISTRICT OF COLUMBIA": "DC",
};

const STATE_CODES = new Set(Object.values(STATE_NAME_TO_CODE));

/**
 * Returns a USPS 2-letter code, or null when the value is not a US state at all
 * (blank, garbage, or a foreign region). Null means "unknown", NOT "invalid" —
 * callers must not treat it as evidence against the coordinate.
 */
export function normalizeState(state) {
  if (!state) return null;
  const raw = String(state).trim().toUpperCase().replace(/\s+/g, " ");
  if (!raw) return null;
  if (STATE_NAME_TO_CODE[raw]) return STATE_NAME_TO_CODE[raw];
  if (STATE_CODES.has(raw)) return raw;
  return null;
}

export function coordinateMatchesState(coord, state) {
  const box = STATE_BBOX[normalizeState(state)];
  if (!box || !isValidNorthAmericaCoord(coord)) return false;
  return (
    coord.lat >= box.latMin && coord.lat <= box.latMax &&
    coord.lng >= box.lngMin && coord.lng <= box.lngMax
  );
}

/**
 * RockPros only operates in AZ / UT / NV / CA. Any order coordinate outside
 * these states is almost certainly a bad geocode or a mistyped client address,
 * so the maps raise a cross-check alert instead of silently plotting it.
 */
export const SERVICE_STATES = ["AZ", "UT", "NV", "CA"];

export function isInServiceArea(coord) {
  if (!isValidNorthAmericaCoord(coord)) return false;
  return SERVICE_STATES.some((s) => coordinateMatchesState(coord, s));
}

/**
 * Checks every coordinate attached to an order (delivery site, quarry, live
 * truck fix) against the service area. Returns [] when all good, otherwise
 * one entry per offending coordinate: { label, coord }.
 */
export function serviceAreaViolations({ delivery, pickup, truck } = {}) {
  const violations = [];
  const check = (label, coord) => {
    if (coord && !isInServiceArea(coord)) violations.push({ label, coord });
  };
  check("delivery site", delivery);
  check("pickup / quarry", pickup);
  check("truck location", truck);
  return violations;
}

/**
 * Returns {plot, reason}. expectedState optional (the order's delivery state).
 *
 * The state check only fires when the site's own state resolves to a US code we
 * hold a bounding box for. An unrecognised state string is a defect in the
 * `customer_site.state` column, not evidence that the coordinate is wrong, so it
 * must not cost the site its pin — the old code rejected those too.
 */
export function shouldPlotCoordinate(coord, expectedState) {
  if (!isValidNorthAmericaCoord(coord)) return { plot: false, reason: "invalid-or-off-continent" };

  const code = normalizeState(expectedState);
  if (expectedState && !code) return { plot: true, reason: "unrecognised-state" };
  if (code && !STATE_BBOX[code]) return { plot: true, reason: `outside-service-area:${code}` };

  if (code && !coordinateMatchesState(coord, code)) {
    return { plot: false, reason: `outside-expected-state:${code}` };
  }
  return { plot: true, reason: "ok" };
}
